import { FORM_TYPE, type FormSchema, onSignUpSubmit } from '@atlas/react-forms';
import cx from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import {
  activeFormsRequest,
  authorizationRequest,
  registerRequest,
} from 'api/forms';
import AuthStaticBlockFallback from 'components/AuthStaticBlockFallback';
import { SignUpEmptyState, SignUpErrorType } from 'components/EmptyStates';
import { Form } from 'components/Form';
import AuthLayout from 'components/Layouts/Auth';
import Loader from 'components/Loader';
import Meta from 'components/Meta';
import StaticBlock from 'components/StaticBlock';
import { useConfig } from 'config';
import { AuthStatus, useClientProfile, useNotifications } from 'store';
import { useAuthRedirects } from 'utils/authRedirects';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation, useTranslation } from 'utils/translations';
import type { FieldValidationStatus } from 'utils/types';

import styles from './SignUp.module.scss';

const formType = FORM_TYPE.CLIENT_REGISTRATION;

const SignUpPage = () => {
  const [errorType, setErrorType] = useState<SignUpErrorType | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { redirectAfterAuth } = useAuthRedirects();
  const [{ status }, { auth }] = useClientProfile();
  const [{ add }] = useNotifications();
  const layout = useLayout();
  const translatedMessage = useTranslation('something_went_wrong');
  const staticBlockName = useConfig(
    `config.siteConfig.authForms.banners.signup.${layout}`,
  );
  const siteName = useConfig('config.siteConfig.general.name');

  useEffect(() => {
    pushToDataLayer('registration_start', btag);
  }, []);

  useEffect(() => {
    if (status === AuthStatus.signedIn) {
      redirectAfterAuth();
    }
  }, [status, redirectAfterAuth]);

  const getActiveForms = useCallback((): Promise<FormSchema[]> => {
    return new Promise((resolve, reject) => {
      activeFormsRequest({
        query: {
          formType,
        },
      })
        .then((data) => {
          if (data.length === 0) {
            throw new Error(SignUpErrorType.NotFound);
          }

          resolve(data);
        })
        .catch((error: Error) => {
          if (error.message === SignUpErrorType.NotFound) {
            setErrorType(SignUpErrorType.NotFound);
          } else {
            setErrorType(SignUpErrorType.ServerError);
          }

          reject(error);
        });
    });
  }, []);

  const handleSubmit = onSignUpSubmit((values) => {
    setIsLoading(true);

    const { id, ...rest } = values;

    return registerRequest({
      body: {
        id: Number(id),
        values: rest,
      },
    })
      .then((data) => {
        pushToDataLayer(
          'registration_completed',
          {
            client: {
              id: data.clientId,
            },
          },
          btag,
        );
        return data;
      })
      .catch(() => {
        add({ text: translatedMessage, type: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  const onSubmitHandler = async (clientData: Record<PropertyKey, unknown>) => {
    if (clientData?.login && clientData?.password) {
      pushToDataLayer('registration_form_button_click', btag);

      const res = await authorizationRequest({
        body: clientData,
      });
      auth(String(res.sessionToken));
      setIsLoading(false);
      redirectAfterAuth();
    }
  };

  const signInLinkState = {
    from:
      history.state?.from ||
      `${window.location.pathname}${window.location.search}`,
  };

  const initialValues = {
    EMAIL: history.state?.data?.email || '',
  };

  const getAsyncValidateFields = useCallback(
    ({ isAsyncValid, name }: FieldValidationStatus) => {
      if (name === 'DOCUMENT_NUMBER' && isAsyncValid) {
        pushToDataLayer('document_filled', btag);
      }
    },
    [],
  );

  return (
    <AuthLayout className={styles['sign-up']} title="Registration">
      <Meta title={<Translation message="title_signup" />} />
      <StaticBlock
        name={staticBlockName}
        fallback={<AuthStaticBlockFallback />}
      />
      <div className={cx(styles.content, styles[layout])}>
        {layout !== LayoutType.mobile && (
          <>
            <h3 className={styles.title}>
              <Translation
                message="signup_welcome_to"
                params={{
                  SITE_NAME: siteName,
                }}
              />
            </h3>
            <p className={styles.description}>
              <Translation message="registration_title" />
            </p>
          </>
        )}
        {isLoading || status !== AuthStatus.signedOut ? (
          <Loader />
        ) : (
          <>
            {errorType && <SignUpEmptyState type={errorType} />}
            {!errorType && (
              <Form
                formType={formType}
                className={styles.form}
                // @ts-ignore
                handleSubmit={handleSubmit}
                submitButtonText="form_submit_register"
                initialValues={initialValues}
                onSubmit={onSubmitHandler}
                onError={() => {}}
                getActiveForms={getActiveForms}
                getAsyncValidateFields={getAsyncValidateFields}
                showErrorFields
                beforeSubmitButton={
                  <div className={styles['submit-info']}>
                    Already have an account?{' '}
                    <Link
                      className={styles.link}
                      to="/signin"
                      state={signInLinkState}
                    >
                      <Translation message="log_in_button" />
                    </Link>
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default SignUpPage;
